import { type ChangeEvent, useEffect, useState } from 'react';
import type { VerseEnv } from '@rb/shared/admin-verses';
import type { NoticeConfig, NoticeItem } from '@rb/shared/notices';
import type { R2Flavor } from '@rb/shared/r2-flavor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  emptyNoticeConfig,
  fetchNoticesFlavored,
  publishNoticesFlavored,
} from '@/master-data/lib/notices';
import { uploadImageToR2 } from '@/master-data/lib/upload-image';
import { useVerseEnvChannel } from '@/verse/use-verse-env-channel';

const CHANNELS = ['dev', 'staging', 'prod'] as const;
const FLAVORS: R2Flavor[] = ['web2', 'web3'];

function newNotice(order: number): NoticeItem {
  return { id: crypto.randomUUID(), active: true, order };
}

function emptyForms(): { web2: NoticeConfig; web3: NoticeConfig } {
  return { web2: emptyNoticeConfig(), web3: emptyNoticeConfig() };
}

export function NoticePanel() {
  const { channel, setChannel, verseEnv } = useVerseEnvChannel();
  const [flavor, setFlavor] = useState<R2Flavor>('web3');
  const [forms, setForms] = useState<{ web2: NoticeConfig; web3: NoticeConfig }>(emptyForms);
  const [status, setStatus] = useState<string>('');
  // R2 로드 성공 여부 — false 면 발행 차단 (로드 실패 시 empty forms 로 sibling flavor 덮어쓰기 방지)
  const [loaded, setLoaded] = useState(false);

  // 현재 flavor 의 폼 config 편의 접근자
  const config = forms[flavor];
  const setConfig = (updater: (prev: NoticeConfig) => NoticeConfig) =>
    setForms((p) => ({ ...p, [flavor]: updater(p[flavor]) }));

  useEffect(() => {
    setLoaded(false);
    void (async () => {
      setStatus('불러오는 중…');
      try {
        setForms(await fetchNoticesFlavored(channel));
        setStatus('');
        setLoaded(true);
      } catch (e) {
        setStatus(`불러오기 실패: ${String(e)}`);
      }
    })();
  }, [channel]);

  const updateAt = (idx: number, patch: Partial<NoticeItem>) =>
    setConfig((c) => ({
      notices: c.notices.map((n, i) => (i === idx ? { ...n, ...patch } : n)),
    }));

  const removeAt = (idx: number) =>
    setConfig((c) => ({ notices: c.notices.filter((_, i) => i !== idx) }));

  const addRow = () =>
    setConfig((c) => ({ notices: [...c.notices, newNotice(c.notices.length + 1)] }));

  // 이미지 업로드는 flavor 무관 — rb-notices 버킷으로 직접 업로드
  const onPickImage = async (
    idx: number,
    field: 'imageUrlKo' | 'imageUrlEn',
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setStatus('이미지 업로드 중…');
    try {
      const url = await uploadImageToR2(f, 'rb-notices');
      updateAt(idx, { [field]: url });
      setStatus('');
    } catch (err) {
      setStatus(`이미지 업로드 실패: ${String(err)}`);
    }
  };

  const onPublish = async () => {
    // R2 로드가 완료되지 않은 상태에서 발행하면 empty forms 로 sibling flavor 가 덮어써진다
    if (!loaded) return;
    if (
      channel === 'prod' &&
      !window.confirm('prod 채널의 라이브 공지 설정을 덮어씁니다. 계속하시겠습니까?')
    ) {
      return;
    }
    setStatus('발행 중…');
    try {
      await publishNoticesFlavored(channel, forms);
      setStatus('발행 완료');
    } catch (e) {
      setStatus(`발행 실패: ${String(e)}`);
    }
  };

  return (
    <section className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold">공지 관리</h1>
        <p className="text-xs text-muted-foreground">
          활성 공지는 order 오름차순으로 로비 진입 시 순차 노출됩니다(1스테이지 클리어 이상 유저).
        </p>
      </header>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="notice-channel">채널</Label>
        <Select value={channel} onValueChange={(v) => setChannel(v as VerseEnv)}>
          <SelectTrigger id="notice-channel" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {verseEnv && (
          <span className="text-xs text-muted-foreground">
            활성 verse 환경({verseEnv})에 자동 동기화됩니다 · 수동 변경 가능
          </span>
        )}
      </div>

      {/* flavor 토글 (web2 | web3) */}
      <div className="flex items-center gap-2">
        <Label>모드</Label>
        <fieldset className="flex rounded-md border m-0 p-0 border-0">
          {FLAVORS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={flavor === f}
              className={`px-4 py-1.5 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                flavor === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-muted text-muted-foreground'
              }`}
              onClick={() => setFlavor(f)}
            >
              {f}
            </button>
          ))}
        </fieldset>
        <span className="text-xs text-muted-foreground">
          발행 시 web2/web3 양쪽 동시 저장됩니다
        </span>
      </div>

      <div className="flex flex-col gap-4">
        {config.notices.map((n, idx) => (
          <Card key={n.id} data-testid={`notice-row-${n.id}`}>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-sm">{n.title || n.id}</CardTitle>
              <Button type="button" variant="destructive" onClick={() => removeAt(idx)}>
                삭제
              </Button>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`active-${n.id}`}
                    checked={n.active}
                    onCheckedChange={(v) => updateAt(idx, { active: v === true })}
                  />
                  <Label htmlFor={`active-${n.id}`}>활성</Label>
                </div>
                <Label htmlFor={`order-${n.id}`}>순서</Label>
                <Input
                  id={`order-${n.id}`}
                  type="number"
                  className="w-24"
                  value={n.order}
                  onChange={(e) => updateAt(idx, { order: Number(e.target.value) })}
                />
              </div>

              {n.imageUrl && !n.imageUrlKo && !n.imageUrlEn && (
                <div className="flex items-center gap-3">
                  <img src={n.imageUrl} alt="" className="h-16 w-16 rounded border object-cover" />
                  <span className="text-xs text-muted-foreground">
                    공통(레거시) 이미지 — ko/en을 새로 올리면 대체됩니다
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3">
                {n.imageUrlKo ? (
                  <img
                    src={n.imageUrlKo}
                    alt=""
                    className="h-16 w-16 rounded border object-cover"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">— 한글 이미지 없음</span>
                )}
                <Label htmlFor={`image-ko-${n.id}`}>한글용</Label>
                <input
                  id={`image-ko-${n.id}`}
                  type="file"
                  accept="image/*"
                  data-testid={`notice-image-ko-input-${n.id}`}
                  onChange={(e) => void onPickImage(idx, 'imageUrlKo', e)}
                />
              </div>

              <div className="flex items-center gap-3">
                {n.imageUrlEn ? (
                  <img
                    src={n.imageUrlEn}
                    alt=""
                    className="h-16 w-16 rounded border object-cover"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">— 영어 이미지 없음</span>
                )}
                <Label htmlFor={`image-en-${n.id}`}>영어용</Label>
                <input
                  id={`image-en-${n.id}`}
                  type="file"
                  accept="image/*"
                  data-testid={`notice-image-en-input-${n.id}`}
                  onChange={(e) => void onPickImage(idx, 'imageUrlEn', e)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`link-${n.id}`}>링크 URL (선택)</Label>
                <Input
                  id={`link-${n.id}`}
                  placeholder="https://…"
                  value={n.linkUrl ?? ''}
                  onChange={(e) => updateAt(idx, { linkUrl: e.target.value || undefined })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`title-${n.id}`}>제목(관리용, 게임 미표시)</Label>
                <Input
                  id={`title-${n.id}`}
                  value={n.title ?? ''}
                  onChange={(e) => updateAt(idx, { title: e.target.value || undefined })}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button type="button" variant="secondary" onClick={addRow}>
          공지 추가
        </Button>
        <Button type="button" onClick={() => void onPublish()} disabled={!loaded}>
          R2 발행
        </Button>
        {status && <p className="text-sm text-muted-foreground">{status}</p>}
      </div>
    </section>
  );
}
